<template>
  <div class="work-page">
    <h1 data-scroll-reveal>Roles I've Had</h1>
    <article data-scroll-group data-scroll-step="110">
      <p>I have worked in university research labs, AI consulting, and digital transformation.</p>
      <ChapterRail :items="chapterItems" label="Work sections" />
      <div id="timeline" data-scroll-group data-scroll-step="140">
        <div data-scroll-group data-scroll-step="160">
          <section v-for="role in roles" :key="role.id" :id="`work-${role.year}`" class="year" data-scroll-group data-scroll-step="170">
            <h3>{{ role.year }}</h3>
            <section :id="role.anchor">
              <h4>{{ role.period }}</h4>
              <ul>
                <li class="image-wrapper work-logo"><img :src="role.media" :alt="role.organisation" :style="{ height: role.logoHeight }" /></li>
                <li class="title">{{ role.title }}</li>
                <li class="role">{{ role.organisation }}</li>
                <li class="location">{{ role.location }}</li>
                <li class="work-summary">{{ role.summary }}</li>
                <li class="work-detail-area">
                  <button type="button" class="work-disclosure" :aria-expanded="!!openRoles[role.id]" :aria-controls="`${role.anchor}-details`" @click="toggleRole(role.id, $event)">
                    <span>{{ openRoles[role.id] ? 'Less detail' : 'Work details' }}</span>
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" /><path class="work-plus" d="M8 3v10" /></svg>
                  </button>
                  <div :id="`${role.anchor}-details`" class="work-detail-panel" :class="{ 'is-open': openRoles[role.id], 'is-instant': instantRole === role.id }" :inert="!openRoles[role.id]" :aria-hidden="!openRoles[role.id]">
                    <div class="work-detail-content">
                      <div v-for="detail in role.details" :key="detail.title" class="work-detail">
                        <h5>
                          <router-link v-if="detail.route" :to="detail.route">{{ detail.title }}<ArrowUpRightIcon /></router-link>
                          <a v-else-if="detail.href" :href="detail.href" target="_blank" rel="noopener noreferrer">{{ detail.title }}<ArrowUpRightIcon /></a>
                          <span v-else>{{ detail.title }}</span>
                        </h5>
                        <p>{{ detail.body }}</p>
                      </div>
                    </div>
                  </div>
                </li>
                <li class="work-view-links"><SourceViewLinks :source-id="`experience:${role.id}`" /></li>
              </ul>
            </section>
          </section>
        </div>
      </div>
    </article>
  </div>
</template>
<script>
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import ChapterRail from './ChapterRail.vue';
import SourceViewLinks from './ui/SourceViewLinks.vue';
import { experienceEntries } from '../data/experienceEntries';
import { settleScrollToId } from '../utils/anchorLanding';
export default {
  name: 'ExperienceTab',
  components: { ChapterRail, SourceViewLinks },
  mixins: [scrollRevealMixin],
  data() {
    return {
      roles: experienceEntries, openRoles: {}, instantRole: '',
      chapterItems: [{ id: 'work-pwc', label: 'PwC' }, { id: 'work-slr', label: 'SLR' }, { id: 'work-hsbc', label: 'HSBC' }, { id: 'work-kiwiview', label: 'Kiwiview' }],
    };
  },
  mounted() { this.scrollToRouteHash(); },
  watch: { '$route.hash'() { this.scrollToRouteHash(); } },
  methods: {
    toggleRole(id, event) {
      this.instantRole = event.detail === 0 ? id : '';
      this.openRoles[id] = !this.openRoles[id];
    },
    scrollToRouteHash(attempt = 0) {
      const hash = this.$route.hash;
      if (!hash) return;
      let id = hash.replace(/^#/, '');
      try { id = decodeURIComponent(id); } catch { /* Keep a plain anchor. */ }
      const role = this.roles.find((entry) => entry.anchor === id);
      if (role) { this.instantRole = role.id; this.openRoles[role.id] = true; }
      this.$nextTick(() => {
        if (settleScrollToId(id)) return;
        if (attempt < 20) window.setTimeout(() => this.scrollToRouteHash(attempt + 1), 80);
      });
    },
  },
};
</script>
<style scoped>
@import url('../assets/css/timeline.css');
#timeline .work-logo { margin: 0; list-style: none; }
#timeline .work-summary { margin-top: 1.3rem; max-width: 43rem; font-size: 1.08em; line-height: 1.8; color: var(--site-text-primary); }
#timeline .work-detail-area { max-width: 43rem; margin-top: .5rem; }
.work-disclosure { display: inline-flex; align-items: center; gap: .75rem; padding: .6rem 0; min-height: 44px; border: 0; background: none; color: var(--site-text-secondary); font: 14px/1.4 'Quattrocento Sans', sans-serif; cursor: var(--site-interactive-cursor); transition: color 180ms ease; }
.work-disclosure svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.2; }
.work-plus { transform-origin: 8px 8px; transition: transform 220ms cubic-bezier(.23,1,.32,1), opacity 160ms ease; }
.work-disclosure[aria-expanded="true"] .work-plus { transform: rotate(90deg); opacity: 0; }
.work-disclosure:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.work-detail-panel { display: grid; grid-template-rows: 0fr; opacity: 0; transition: grid-template-rows 260ms cubic-bezier(.23,1,.32,1), opacity 180ms ease; }
.work-detail-panel.is-open { grid-template-rows: 1fr; opacity: 1; }
.work-detail-content { min-height: 0; overflow: hidden; }
.work-detail { padding: 1.2rem 0 1.4rem; border-top: 1px solid var(--site-border-subtle, rgba(128,128,128,.2)); }
.work-detail:first-child { margin-top: .7rem; }
.work-detail h5 { margin: 0 0 .5rem; font: 600 16px/1.45 'Quattrocento Sans', sans-serif; color: var(--site-text-primary); }
.work-detail h5 a { display: inline-flex; align-items: baseline; gap: 8px; color: inherit; }
.work-detail h5 svg { width: 13px; height: 13px; flex: 0 0 auto; align-self: center; transition: transform 180ms cubic-bezier(.23,1,.32,1); }
.work-detail p { margin: 0; color: var(--site-text-secondary); line-height: 1.8; }
.work-view-links { list-style: none; }
.work-detail-panel.is-instant { transition: none; }
@media (hover: hover) and (pointer: fine) { .work-disclosure:hover { color: var(--site-text-primary); } .work-detail h5 a:hover svg { transform: translate(2px,-2px); } }
@media (prefers-reduced-motion: reduce) { .work-detail-panel, .work-plus, .work-disclosure, .work-detail h5 svg { transition: none; } }
</style>
